// src/components/dashboard/WeatherStationDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Thermometer, Droplets, Wind, CloudRain, Sun, Gauge,
  Activity, ArrowUp, ArrowDown, Clock,
  Battery, Wifi, MapPin, Calendar, TrendingUp, AlertCircle,
  Waves, Mountain, RefreshCw, ArrowLeft, X, Maximize2,
  Key, Copy, Lock, ChevronRight, Eye, EyeOff,
} from 'lucide-react';
import { LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { aggregateDataByHour, aggregateDataByDay, filterLastHours } from '../../services/weatherDataService';
import { fetchMyApiKeys, generateMyApiKey, revokeApiKey } from '../../services/adminService';
import { API_BASE_URL } from '../../services/config';
import SoilSensorPanel from './SoilSensorPanel';

// Unified chart parameter definitions
const CHART_PARAMS = [
  { key: 'temperature', label: 'Temperature', unit: '°C',  color: '#ef4444', range: [0, 45] },
  { key: 'humidity',    label: 'Humidity',    unit: '%',   color: '#3b82f6', range: [0, 100] },
  { key: 'windSpeed',   label: 'Wind Speed',  unit: 'm/s', color: '#10b981', range: [0, 20] },
  { key: 'pressure',    label: 'Pressure',    unit: 'hPa', color: '#7c3aed', range: [970, 1030] },
  { key: 'rainfall',    label: 'Rainfall',    unit: 'mm',  color: '#0891b2', range: [0, 50] },
  { key: 'uvIndex',     label: 'UV Index',    unit: '',    color: '#f59e0b', range: [0, 12] },
];

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3 text-xs min-w-[160px]">
      <p className="font-semibold text-gray-700 mb-2 border-b border-gray-100 pb-1.5">{label}</p>
      {payload.map(entry => {
        const param = CHART_PARAMS.find(p => p.key === entry.dataKey);
        const raw = entry.payload[`_raw_${entry.dataKey}`];
        return (
          <div key={entry.dataKey} className="flex items-center justify-between gap-3 mb-1">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
              <span className="text-gray-500">{param?.label}</span>
            </div>
            <span className="font-bold text-gray-900 tabular-nums">
              {raw != null ? `${Number(raw).toFixed(1)}${param?.unit}` : '—'}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const WeatherStationDashboard = ({ stationId, stationInfo, onBack, userData, userRole }) => {
  const [weatherData, setWeatherData] = useState(null);
  const [historicalData, setHistoricalData] = useState([]); // Hourly aggregated for 24h chart
  const [rawData, setRawData] = useState([]); // Raw data for modal details
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval] = useState(30000); // 30 seconds
  const [selectedParameter, setSelectedParameter] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [aggregationType, setAggregationType] = useState('raw'); // 'raw', 'hourly', 'daily'

  // Unified chart active parameters
  const [activeParams, setActiveParams] = useState(new Set(['temperature', 'humidity', 'windSpeed']));

  // API key panel
  const hasApiAccess = userData && (
    ['research', 'enterprise', 'custom'].includes(userData.accessTier) ||
    userData.role === 'custodian'
  );
  const [apiKeys,       setApiKeys]       = useState([]);
  const [apiKeyBusy,    setApiKeyBusy]    = useState(false);
  const [showApiPanel,  setShowApiPanel]  = useState(false);
  const [newKeySecret,  setNewKeySecret]  = useState(null); // shown once
  const [keyVisible,    setKeyVisible]    = useState(false);

  useEffect(() => {
    fetchLatestData();
    
    if (autoRefresh) {
      const interval = setInterval(fetchLatestData, refreshInterval);
      return () => clearInterval(interval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId, autoRefresh, refreshInterval]);

  const loadMyKeys = useCallback(async () => {
    if (!hasApiAccess) return;
    try {
      const res = await fetchMyApiKeys();
      setApiKeys((res.keys || []).filter(k => k.isActive && (k.deviceIds || []).includes(stationId)));
    } catch { /* silently ignore */ }
  }, [hasApiAccess, stationId]);

  useEffect(() => { if (showApiPanel) loadMyKeys(); }, [showApiPanel, loadMyKeys]);

  const handleGenerateKey = async () => {
    setApiKeyBusy(true);
    try {
      const res = await generateMyApiKey(`${stationInfo?.name || stationId} Key`);
      setNewKeySecret(res.key);
      loadMyKeys();
    } catch (e) { alert(e.message || 'Failed to generate key'); }
    finally { setApiKeyBusy(false); }
  };

  const handleRevokeKey = async (keyId) => {
    try {
      await revokeApiKey(keyId);
      setApiKeys(k => k.filter(x => x.id !== keyId));
    } catch (e) { alert(e.message || 'Failed to revoke key'); }
  };

  const fetchLatestData = async () => {
    try {
      setLoading(true);
      console.log(`Fetching data for station ${stationId} from TTN API...`);

      const TTN_BASE  = 'https://api.wirelessplanet.co.ke/api/v1';
      const TTN_KEY   = 'a19a464df82de32f4c238b02060f1c2d8ae8ae968d3e2401142fce4882ff1d54';
      const headers   = { 'X-API-Key': TTN_KEY };

      // Fetch up to 7 days of readings (stations report every ~15-30 min, so 7d ≈ 672 readings)
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
      const res = await fetch(
        `${TTN_BASE}/readings/device/${stationId}?limit=700&startDate=${sevenDaysAgo}`,
        { headers }
      );

      if (!res.ok) throw new Error(`Failed to fetch data (${res.status})`);

      const json = await res.json();
      const raw  = json.data || [];

      if (raw.length === 0) throw new Error('No data available for this station');

      // Map TTN sensorData fields → app field names
      const mapR = (r) => ({
        timestamp:    r.receivedAt,
        temperature:  r.sensorData?.airTemperature     ?? null,
        humidity:     r.sensorData?.airHumidity         ?? null,
        pressure:     r.sensorData?.barometricPressure != null ? r.sensorData.barometricPressure / 100 : null,
        wind_speed:   r.sensorData?.windSpeed           ?? null,
        wind_dir:     r.sensorData?.windDirectionSensor ?? null,
        peak_gust:    r.sensorData?.peakWindGust        ?? null,
        rain_gauge:   r.sensorData?.rainGauge           ?? null,
        rain_accum:   r.sensorData?.rainAccumulation    ?? null,
        uv_index:     r.sensorData?.uvIndex             ?? null,
        light:        r.sensorData?.lightIntensity      ?? null,
        rssi:         r.gateway?.rssi    ?? null,
        snr:          r.gateway?.snr     ?? null,
      });

      const mapped = raw.map(mapR);
      // Latest reading first
      setWeatherData(mapped[0]);

      // Build chart-ready records (chronological order)
      const allStationData = [...mapped].reverse().map(r => ({
        ...r,
        time: new Date(r.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        windSpeed: r.wind_speed || 0,
        rainfall:  r.rain_gauge || 0,
        uvIndex:   r.uv_index  || 0,
      }));

      setRawData(allStationData);

      const hourlyAggregated = aggregateDataByHour(allStationData, 24);
      setHistoricalData(hourlyAggregated);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error('Error fetching weather data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getWindDirection = (degrees) => {
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 
                       'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
  };

  const getUVLevel = (index) => {
    if (index <= 2) return { level: 'Low', color: 'green' };
    if (index <= 5) return { level: 'Moderate', color: 'yellow' };
    if (index <= 7) return { level: 'High', color: 'orange' };
    if (index <= 10) return { level: 'Very High', color: 'red' };
    return { level: 'Extreme', color: 'purple' };
  };

  // Compute pressure trend from last 3 readings
  const getPressureTrend = () => {
    const recent = historicalData.slice(-3).map(d => d.pressure).filter(Boolean);
    if (recent.length < 2) return { label: 'Stable', up: null };
    const delta = recent[recent.length - 1] - recent[0];
    if (delta > 0.5) return { label: 'Rising', up: true };
    if (delta < -0.5) return { label: 'Falling', up: false };
    return { label: 'Stable', up: null };
  };

  // Data aggregation functions
  const aggregateData = (data, parameterType, aggregationType) => {
    if (aggregationType === 'raw') {
      // For raw data, show only last 1 hour
      return filterLastHours(data, 1);
    }

    if (aggregationType === 'hourly') {
      // Show hourly averages for last 24 hours (exactly 24 slots)
      return aggregateDataByHour(data, 24);
    }

    if (aggregationType === 'daily') {
      // Show daily averages for last 7 days (exactly 7 slots)
      return aggregateDataByDay(data, 7);
    }

    return data;
  };

  const handleParameterClick = (parameterType, parameterName, parameterIcon, parameterColor) => {
    setSelectedParameter({
      type: parameterType,
      name: parameterName,
      icon: parameterIcon,
      color: parameterColor,
      data: rawData // Use raw data for modal
    });
    setAggregationType('raw'); // Reset to raw data when opening modal
    setShowDetailModal(true);
  };

  const handleGoBack = () => {
    if (onBack) {
      onBack();
    } else {
      window.history.back();
    }
  };

  if (loading && !weatherData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading weather station data...</p>
        </div>
      </div>
    );
  }

  if (error && !weatherData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl border border-red-200 overflow-hidden">
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4">
              <div className="flex items-center space-x-3">
                <AlertCircle className="w-8 h-8 text-white" />
                <h2 className="text-xl font-bold text-white">Error Loading Data</h2>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">{error}</p>
              <div className="flex space-x-3">
                <button
                  onClick={fetchLatestData}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <RefreshCw className="w-5 h-5" />
                  <span>Retry</span>
                </button>
                <button
                  onClick={handleGoBack}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 font-semibold transition-all duration-300"
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1600px] mx-auto p-3 sm:p-5 xl:p-6 space-y-3 sm:space-y-5">

        {/* ── Station Header ─────────────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-700 via-green-800 to-teal-900 px-4 sm:px-6 py-4 sm:py-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <button onClick={handleGoBack}
                  className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-xl flex items-center justify-center border border-white/30 transition-all shrink-0">
                  <ArrowLeft className="w-5 h-5 text-white" />
                </button>
                <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center border border-white/30 shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white tracking-tight">
                    {stationInfo?.name || `Station ${stationId}`}
                  </h1>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <div className="flex items-center gap-1.5 text-blue-100">
                      <Clock className="w-3.5 h-3.5" />
                      <span className="text-xs">Updated: {lastUpdate.toLocaleTimeString()}</span>
                    </div>
                    {weatherData && (
                      <div className="flex items-center gap-1.5 bg-green-500 px-2.5 py-0.5 rounded-full">
                        <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                        <span className="text-[10px] font-bold text-white">LIVE</span>
                      </div>
                    )}
                    <div className="hidden sm:flex items-center gap-2 text-blue-200 text-xs">
                      <MapPin className="w-3 h-3" />
                      <span>{stationInfo?.location || 'Unknown'}</span>
                      {stationInfo?.altitude > 0 && (
                        <><Mountain className="w-3 h-3 ml-1" /><span>{stationInfo.altitude} m</span></>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => setAutoRefresh(!autoRefresh)}
                  className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all flex items-center gap-1.5 ${
                    autoRefresh ? 'bg-white text-emerald-800' : 'bg-white/20 text-white border border-white/30'
                  }`}>
                  <Activity className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden xs:inline">Auto: {autoRefresh ? 'ON' : 'OFF'}</span>
                  <span className="xs:hidden">{autoRefresh ? 'ON' : 'OFF'}</span>
                </button>
                <button onClick={fetchLatestData} disabled={loading}
                  className="px-3 py-1.5 sm:px-4 sm:py-2 bg-white text-emerald-800 rounded-xl text-xs sm:text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50">
                  <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${loading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Main two-column layout ─────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_290px] gap-3 sm:gap-5 items-start">

          {/* LEFT: KPI cards + 24h trend chart */}
          <div className="space-y-3 sm:space-y-5">

            {/* 4 KPI metric cards — 2×2 on mobile, 2×2 on desktop */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">

              {/* Temperature */}
              <div onClick={() => handleParameterClick('temperature', 'Temperature', Thermometer, 'red')}
                className="group bg-white rounded-2xl border border-gray-200 p-3 sm:p-5 cursor-pointer hover:border-gray-300 hover:shadow-md transition-all">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Thermometer className="w-4 h-4 text-gray-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Temperature</span>
                  </div>
                  <Maximize2 className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors" />
                </div>
                <p className="text-2xl sm:text-4xl font-black text-gray-900 mb-0.5">{weatherData?.temperature?.toFixed(1) ?? '—'}°C</p>
                <p className="text-xs text-gray-400 mb-3">Air Temperature</p>
                <div className="h-14 -mx-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={historicalData.slice(-10)}>
                      <defs>
                        <linearGradient id="tGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15}/>
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <Area type="monotone" dataKey="temperature" stroke="#6366f1" fill="url(#tGrad)" strokeWidth={2} dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Humidity */}
              <div onClick={() => handleParameterClick('humidity', 'Humidity', Droplets, 'blue')}
                className="group bg-white rounded-2xl border border-gray-200 p-3 sm:p-5 cursor-pointer hover:border-gray-300 hover:shadow-md transition-all">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Droplets className="w-4 h-4 text-gray-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Humidity</span>
                  </div>
                  <Maximize2 className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors" />
                </div>
                <p className="text-2xl sm:text-4xl font-black text-gray-900 mb-0.5">{weatherData?.humidity?.toFixed(0) ?? '—'}%</p>
                <p className="text-xs text-gray-400 mb-4">Relative Humidity</p>
                <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden mb-1">
                  <div className="h-full bg-gray-700 rounded-full transition-all duration-500"
                    style={{ width: `${weatherData?.humidity || 0}%` }} />
                </div>
                <div className="flex justify-between text-[10px] text-gray-400">
                  <span>Dry</span><span className="font-semibold">40–60% optimal</span><span>Humid</span>
                </div>
              </div>

              {/* Wind Speed */}
              <div onClick={() => handleParameterClick('windSpeed', 'Wind Speed', Wind, 'green')}
                className="group bg-white rounded-2xl border border-gray-200 p-3 sm:p-5 cursor-pointer hover:border-gray-300 hover:shadow-md transition-all">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Wind className="w-4 h-4 text-gray-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Wind Speed</span>
                  </div>
                  <Maximize2 className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors" />
                </div>
                <p className="text-2xl sm:text-4xl font-black text-gray-900 mb-0.5">
                  {weatherData?.wind_speed?.toFixed(1) ?? '—'} <span className="text-lg font-semibold text-gray-400">m/s</span>
                </p>
                <p className="text-xs text-gray-400 mb-4">
                  {(weatherData?.wind_speed ?? 0) < 1 ? 'Calm' : (weatherData?.wind_speed ?? 0) < 5 ? 'Light Breeze' : (weatherData?.wind_speed ?? 0) < 11 ? 'Moderate' : 'Strong'}
                </p>
                <div className="flex gap-2">
                  <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                    <p className="text-[10px] text-gray-400 mb-0.5">Direction</p>
                    <p className="text-xs font-bold text-gray-800">{getWindDirection(weatherData?.wind_dir || 0)} · {weatherData?.wind_dir || 0}°</p>
                  </div>
                  {weatherData?.peak_gust != null && (
                    <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                      <p className="text-[10px] text-gray-400 mb-0.5">Peak Gust</p>
                      <p className="text-xs font-bold text-gray-800">{weatherData.peak_gust.toFixed(1)} m/s</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Pressure */}
              <div onClick={() => handleParameterClick('pressure', 'Atmospheric Pressure', Gauge, 'purple')}
                className="group bg-white rounded-2xl border border-gray-200 p-3 sm:p-5 cursor-pointer hover:border-gray-300 hover:shadow-md transition-all">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Gauge className="w-4 h-4 text-gray-600" />
                    </div>
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pressure</span>
                  </div>
                  <Maximize2 className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors" />
                </div>
                <p className="text-2xl sm:text-4xl font-black text-gray-900 mb-0.5">
                  {weatherData?.pressure?.toFixed(0) ?? '—'} <span className="text-lg font-semibold text-gray-400">hPa</span>
                </p>
                <p className="text-xs text-gray-400 mb-4">Barometric Pressure</p>
                {(() => {
                  const trend = getPressureTrend();
                  return (
                    <div className="flex items-center gap-2 text-sm">
                      {trend.up === true  ? <ArrowUp className="w-4 h-4 text-gray-500" /> :
                       trend.up === false ? <ArrowDown className="w-4 h-4 text-gray-500" /> :
                                           <Activity className="w-4 h-4 text-gray-400" />}
                      <span className="font-semibold text-gray-700">{trend.label}</span>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* ── Unified Trends Chart ──────────────────────────────────────────────── */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-700">Station Trends</span>
                  <span className="text-xs text-gray-400 hidden sm:inline">— select parameters below</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-xs text-gray-500">Live</span>
                </div>
              </div>

              {/* Parameter toggle pills */}
              <div className="px-6 pt-4 pb-2 flex flex-wrap gap-2">
                {CHART_PARAMS.map(p => {
                  const on = activeParams.has(p.key);
                  return (
                    <button key={p.key}
                      onClick={() => setActiveParams(prev => {
                        const next = new Set(prev);
                        next.has(p.key) ? next.delete(p.key) : next.add(p.key);
                        return next;
                      })}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border"
                      style={on
                        ? { backgroundColor: p.color, borderColor: p.color, color: '#fff' }
                        : { backgroundColor: '#fff', borderColor: '#e5e7eb', color: '#6b7280' }}>
                      <span className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: on ? '#fff' : p.color }} />
                      {p.label}
                    </button>
                  );
                })}
              </div>

              {/* Chart */}
              <div className="px-5 pb-5">
                <div className="h-80">
                  {historicalData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={historicalData.map(d => {
                          const row = { time: d.time };
                          CHART_PARAMS.forEach(({ key, range }) => {
                            const v = d[key];
                            row[key] = v != null ? Math.max(0, Math.min(100, ((v - range[0]) / (range[1] - range[0])) * 100)) : null;
                            row[`_raw_${key}`] = v;
                          });
                          return row;
                        })}
                        margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="time" stroke="#9ca3af" fontSize={11} tickLine={false} />
                        <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false}
                          domain={[0, 100]} tickFormatter={v => `${v}%`} width={38} />
                        <Tooltip content={<ChartTooltip />} />
                        {CHART_PARAMS.filter(p => activeParams.has(p.key)).map(p => (
                          <Line key={p.key} type="monotone" dataKey={p.key}
                            stroke={p.color} strokeWidth={2} dot={false} connectNulls={true} activeDot={{ r: 4 }} />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-sm text-gray-400">
                      Loading chart data…
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-gray-400 text-center mt-1">
                  Trends normalized to 0–100% for comparison · hover for actual values
                </p>
              </div>
            </div>

          </div>

          {/* RIGHT sidebar */}
          <div className="space-y-4">

            {/* Rainfall */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center">
                  <CloudRain className="w-3.5 h-3.5 text-gray-600" />
                </div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Rainfall</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[10px] text-gray-400 uppercase font-semibold mb-1">Current</p>
                  <p className="text-2xl font-black text-gray-900">{(weatherData?.rain_gauge ?? 0).toFixed(2)}</p>
                  <p className="text-xs text-gray-400">mm</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[10px] text-gray-400 uppercase font-semibold mb-1">Accumulated</p>
                  <p className="text-2xl font-black text-gray-900">{(weatherData?.rain_accum ?? 0).toFixed(1)}</p>
                  <p className="text-xs text-gray-400">mm</p>
                </div>
              </div>
            </div>

            {/* UV Index */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Sun className="w-3.5 h-3.5 text-gray-600" />
                  </div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">UV Index</span>
                </div>
                <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  {getUVLevel(weatherData?.uv_index || 0).level}
                </span>
              </div>
              <p className="text-3xl font-black text-gray-900 mb-3">{(weatherData?.uv_index ?? 0).toFixed(1)}</p>
              <div className="relative w-full h-2 rounded-full overflow-hidden bg-gradient-to-r from-green-300 via-yellow-300 via-orange-400 to-red-500">
                <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-2 border-gray-400 rounded-full shadow"
                  style={{ left: `${Math.min(93, (weatherData?.uv_index || 0) / 11 * 100)}%` }} />
              </div>
              <div className="flex justify-between text-[10px] text-gray-400 mt-1.5">
                <span>0</span><span>3</span><span>6</span><span>8</span><span>11+</span>
              </div>
            </div>

            {/* Light Intensity */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Sun className="w-3.5 h-3.5 text-gray-600" />
                </div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Light Intensity</span>
              </div>
              <p className="text-3xl font-black text-gray-900 mb-0.5">{((weatherData?.light ?? 0) / 1000).toFixed(1)}</p>
              <p className="text-xs text-gray-400 mb-3">klux</p>
              <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden mb-1.5">
                <div className="h-full bg-gray-600 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(100, ((weatherData?.light ?? 0) / 100000) * 100)}%` }} />
              </div>
              <p className="text-xs text-gray-500 font-medium">
                {(weatherData?.light ?? 0) > 50000 ? 'Bright Sunlight' :
                 (weatherData?.light ?? 0) > 20000 ? 'Daylight' :
                 (weatherData?.light ?? 0) > 10000 ? 'Overcast' :
                 (weatherData?.light ?? 0) > 1000  ? 'Dim' : 'Night'}
              </p>
            </div>

            {/* Station Health */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                <Activity className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-semibold text-gray-700">Station Health</span>
              </div>
              <div className="divide-y divide-gray-50">
                {[
                  { icon: Battery,  label: 'Battery', value: weatherData?.battery_voltage != null ? `${weatherData.battery_voltage.toFixed(2)} V` : '—' },
                  ...(userRole === 'super_admin' ? [
                    { icon: Wifi,     label: 'RSSI',    value: weatherData?.rssi != null ? `${weatherData.rssi} dBm` : '—' },
                    { icon: Activity, label: 'SNR',     value: weatherData?.snr  != null ? `${weatherData.snr} dB`  : '—' },
                  ] : []),
                  { icon: Activity, label: 'Status',  value: 'Online', badge: true },
                ].map(({ icon: Icon, label, value, badge }) => (
                  <div key={label} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <Icon className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-sm text-gray-600">{label}</span>
                    </div>
                    {badge
                      ? <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">{value}</span>
                      : <span className="text-sm font-bold text-gray-900">{value}</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Station Info */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-semibold text-gray-700">Station Info</span>
              </div>
              <div className="divide-y divide-gray-50">
                {[
                  { icon: MapPin,   label: 'Location',   value: stationInfo?.location || 'Unknown' },
                  { icon: Mountain, label: 'Elevation',  value: `${stationInfo?.altitude || 0} m` },
                  { icon: Calendar, label: 'Station ID', value: stationId?.slice(-12), mono: true },
                ].map(({ icon: Icon, label, value, mono }) => (
                  <div key={label} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <Icon className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-sm text-gray-600">{label}</span>
                    </div>
                    <span className={`text-sm font-bold text-gray-900 text-right max-w-[150px] truncate ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Soil Conditions ───────────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
            <Waves className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-semibold text-gray-700">Soil Conditions</span>
            <span className="text-xs text-gray-400">— Agricultural monitoring</span>
          </div>
          <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Soil Moisture',     suffix: '%',  s1: weatherData?.soil_moisture1,    s2: weatherData?.soil_moisture2,    max: 100, icon: Droplets },
              { label: 'Soil Temperature',  suffix: '°C', s1: weatherData?.soil_temperature1, s2: weatherData?.soil_temperature2, max: 40,  icon: Thermometer },
            ].flatMap(({ label, suffix, s1, s2, max, icon: Icon }) =>
              [{ val: s1, n: 1 }, { val: s2, n: 2 }].map(({ val, n }) => (
                <div key={`${label}-${n}`} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-gray-500" />
                      <span className="text-xs text-gray-500 font-medium">{label}</span>
                    </div>
                    <span className="text-[10px] text-gray-400 bg-white border border-gray-200 px-2 py-0.5 rounded-full">S{n}</span>
                  </div>
                  <p className="text-2xl font-black text-gray-900 mb-2">{val?.toFixed(1) ?? '—'}{val != null ? suffix : ''}</p>
                  <div className="w-full bg-gray-200 rounded-full h-1 overflow-hidden">
                    <div className="h-full bg-gray-500 rounded-full" style={{ width: `${Math.min(100, ((val ?? 0) / max) * 100)}%` }} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── API Access Panel ──────────────────────────────────────────────────── */}
        <div>
          <button onClick={() => setShowApiPanel(v => !v)}
            className="w-full flex items-center justify-between p-5 bg-white rounded-2xl border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${hasApiAccess ? 'bg-gradient-to-br from-purple-500 to-indigo-600' : 'bg-gray-100'}`}>
                {hasApiAccess ? <Key className="w-5 h-5 text-white" /> : <Lock className="w-5 h-5 text-gray-500" />}
              </div>
              <div className="text-left">
                <p className="font-bold text-gray-900 text-sm">Developer API Access</p>
                <p className="text-xs text-gray-500">
                  {hasApiAccess ? `Programmatic access to this station's data` : 'Upgrade your plan to access the data API'}
                </p>
              </div>
            </div>
            <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${showApiPanel ? 'rotate-90' : ''}`} />
          </button>

          {showApiPanel && (
            <div className="mt-3 bg-white rounded-2xl border border-gray-200 overflow-hidden">
              {hasApiAccess ? (
                <div className="p-6 space-y-5">
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Proxy Endpoint for this station</p>
                    <div className="bg-gray-900 rounded-xl p-4 font-mono text-sm space-y-1.5">
                      <p><span className="text-blue-400">GET</span> <span className="text-green-400">{API_BASE_URL}/data/readings/{stationId}</span></p>
                      <p><span className="text-blue-400">GET</span> <span className="text-green-400">{API_BASE_URL}/data/readings/{stationId}/latest</span></p>
                      <p className="text-gray-400 text-xs mt-2">Auth: <span className="text-yellow-400">Authorization: ApiKey {'<your-key>'}</span></p>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-bold text-gray-800">Your API Keys for this station</p>
                      <button onClick={handleGenerateKey} disabled={apiKeyBusy}
                        className="text-xs px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1.5">
                        {apiKeyBusy ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Key className="w-3 h-3" />}
                        Generate Key
                      </button>
                    </div>
                    {newKeySecret && (
                      <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                        <p className="text-xs font-bold text-yellow-800 mb-2">⚠ Save this key now — it won't be shown again</p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 bg-gray-900 text-green-400 text-xs rounded-lg p-2.5 font-mono break-all select-all">
                            {keyVisible ? newKeySecret : newKeySecret.slice(0, 12) + '•'.repeat(30)}
                          </code>
                          <button onClick={() => setKeyVisible(v => !v)} className="p-2 text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg">
                            {keyVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button onClick={() => navigator.clipboard.writeText(newKeySecret)} className="p-2 text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg">
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                        <button onClick={() => setNewKeySecret(null)} className="mt-2 text-xs text-yellow-700 underline">Done, I've saved it</button>
                      </div>
                    )}
                    {apiKeys.length > 0 ? (
                      <div className="space-y-2">
                        {apiKeys.map(k => (
                          <div key={k.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                            <div>
                              <p className="text-xs font-semibold text-gray-700">{k.name}</p>
                              <code className="text-xs text-gray-500 font-mono">{k.keyPrefix}••••••••••••</code>
                              <p className="text-[10px] text-gray-400 mt-0.5">
                                Last used: {k.lastUsed ? new Date(k.lastUsed).toLocaleDateString() : 'Never'} · {k.requestCount || 0} requests
                              </p>
                            </div>
                            <button onClick={() => handleRevokeKey(k.id)}
                              className="text-xs px-2.5 py-1.5 text-red-600 border border-red-200 rounded-lg hover:bg-red-50">
                              Revoke
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : !newKeySecret && (
                      <p className="text-sm text-gray-400 text-center py-4">No active keys for this station. Generate one above.</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Example — cURL</p>
                    <div className="bg-gray-900 rounded-xl p-4 font-mono text-xs text-green-400 relative">
                      <button onClick={() => navigator.clipboard.writeText(`curl -H "Authorization: ApiKey YOUR_KEY" \\\n  ${API_BASE_URL}/data/readings/${stationId}/latest`)}
                        className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-white">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <p className="text-gray-400"># Fetch latest reading</p>
                      <p>curl -H <span className="text-yellow-400">"Authorization: ApiKey YOUR_KEY"</span> \</p>
                      <p className="pl-4 text-green-300">{API_BASE_URL}/data/readings/{stationId}/latest</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">API Access Not Included</h3>
                  <p className="text-sm text-gray-500 mb-1">
                    Your current plan (<strong className="text-gray-700 capitalize">{userData?.accessTier || 'Basic'}</strong>) does not include programmatic API access.
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    Upgrade to <strong>Research</strong>, <strong>Enterprise</strong>, or <strong>Custom</strong> to get a per-device API key and access real-time data programmatically.
                  </p>
                  <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto mb-6">
                    {[
                      { label: 'Research',   price: 'KES 2,500/mo', color: 'purple' },
                      { label: 'Enterprise', price: 'Custom',       color: 'green' },
                      { label: 'Custom',     price: 'KES 1,000+',   color: 'orange' },
                    ].map(t => (
                      <div key={t.label} className={`p-3 rounded-xl border-2 ${
                        t.color === 'purple' ? 'border-purple-200 bg-purple-50' :
                        t.color === 'green'  ? 'border-green-200 bg-green-50' :
                                               'border-orange-200 bg-orange-50'
                      }`}>
                        <p className="text-xs font-bold text-gray-800">{t.label}</p>
                        <p className="text-[10px] text-gray-500">{t.price}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400">Contact your administrator to upgrade your access tier.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Soil Sensor Panel (shown when a colocated soil sensor exists) ─────── */}
        <SoilSensorPanel weatherStationId={stationId} />

      </div>

      {/* ── Parameter Detail Modal ─────────────────────────────────────────────── */}
      {showDetailModal && selectedParameter && (() => {
        const MODAL_COLORS = {
          red:    { hex: '#ef4444', light: '#fff1f2', border: '#fecdd3', dark: '#dc2626', grad1: '#f87171', grad2: '#dc2626' },
          blue:   { hex: '#3b82f6', light: '#eff6ff', border: '#bfdbfe', dark: '#2563eb', grad1: '#60a5fa', grad2: '#2563eb' },
          green:  { hex: '#10b981', light: '#f0fdf4', border: '#a7f3d0', dark: '#059669', grad1: '#34d399', grad2: '#059669' },
          purple: { hex: '#8b5cf6', light: '#f5f3ff', border: '#ddd6fe', dark: '#7c3aed', grad1: '#a78bfa', grad2: '#7c3aed' },
          cyan:   { hex: '#0891b2', light: '#ecfeff', border: '#a5f3fc', dark: '#0e7490', grad1: '#22d3ee', grad2: '#0e7490' },
          amber:  { hex: '#f59e0b', light: '#fffbeb', border: '#fde68a', dark: '#d97706', grad1: '#fbbf24', grad2: '#d97706' },
        };
        const col = MODAL_COLORS[selectedParameter.color] || MODAL_COLORS.blue;
        const chartData = aggregateData(selectedParameter.data, selectedParameter.type, aggregationType);
        const vals = chartData.map(d => d[selectedParameter.type]).filter(v => v != null && !isNaN(v));
        // latest reading (rawData has windSpeed mapped from wind_speed)
        const latestRaw = selectedParameter.data[selectedParameter.data.length - 1];
        const currentVal = latestRaw?.[selectedParameter.type] ?? (vals.length ? vals[vals.length - 1] : null);
        const maxVal = vals.length ? Math.max(...vals) : null;
        const minVal = vals.length ? Math.min(...vals) : null;
        const avgVal = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
        const paramCfg = CHART_PARAMS.find(p => p.key === selectedParameter.type);
        const unit = paramCfg?.unit ?? '';
        const fmtVal = v => v != null && !isNaN(v) ? `${Number(v).toFixed(1)}${unit}` : '—';
        const RANGE_META = {
          raw:    { label: '1 Hour',    sub: 'Raw readings · every ~15 min' },
          hourly: { label: '24 Hours',  sub: 'Hourly averages' },
          daily:  { label: '7 Days',    sub: 'Daily averages' },
        };
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowDetailModal(false)}>
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
              onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div className="relative px-7 py-5 flex items-center justify-between shrink-0"
                style={{ background: `linear-gradient(135deg, ${col.grad1}, ${col.grad2})` }}>
                <div className="absolute inset-0 opacity-10 pointer-events-none"
                  style={{ backgroundImage: 'radial-gradient(circle at 85% 15%, white 0%, transparent 55%)' }} />
                <div className="relative flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center border-2 border-white/30"
                    style={{ background: 'rgba(255,255,255,0.18)' }}>
                    {React.createElement(selectedParameter.icon, { className: 'w-6 h-6 text-white' })}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white leading-tight">{selectedParameter.name}</h2>
                    <p className="text-white/65 text-xs mt-0.5">Historical Data & Trends</p>
                  </div>
                </div>
                <button onClick={() => setShowDetailModal(false)}
                  className="relative w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:scale-110"
                  style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)' }}>
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>

              {/* Body */}
              <div className="overflow-y-auto flex-1 p-6 space-y-5 bg-gray-50/60">

                {/* Range tabs */}
                <div className="flex items-center gap-1 p-1 bg-white rounded-xl border border-gray-100 shadow-sm w-fit">
                  {[
                    { k: 'raw',    label: '1 Hour' },
                    { k: 'hourly', label: '24 Hours' },
                    { k: 'daily',  label: '7 Days' },
                  ].map(({ k, label }) => (
                    <button key={k} onClick={() => setAggregationType(k)}
                      className="px-5 py-2 rounded-lg text-sm font-semibold transition-all"
                      style={aggregationType === k
                        ? { backgroundColor: col.hex, color: '#fff', boxShadow: `0 2px 10px ${col.hex}55` }
                        : { color: '#6b7280', background: 'transparent' }}>
                      {label}
                    </button>
                  ))}
                </div>

                {/* Stats cards — 4 columns */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Current', val: currentVal, sub: 'Latest reading' },
                    { label: 'Maximum', val: maxVal,     sub: `In ${RANGE_META[aggregationType].label.toLowerCase()}` },
                    { label: 'Minimum', val: minVal,     sub: `In ${RANGE_META[aggregationType].label.toLowerCase()}` },
                    { label: 'Average', val: avgVal,     sub: `Over ${vals.length} pts` },
                  ].map(({ label, val, sub }) => (
                    <div key={label} className="bg-white rounded-2xl p-4 border shadow-sm"
                      style={{ borderColor: col.border }}>
                      <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5"
                        style={{ color: col.dark }}>{label}</p>
                      <p className="text-2xl font-black text-gray-900 tabular-nums leading-none mt-1">
                        {fmtVal(val)}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-1.5">{sub}</p>
                    </div>
                  ))}
                </div>

                {/* Chart */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-sm font-bold text-gray-800">{RANGE_META[aggregationType].label}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{RANGE_META[aggregationType].sub} · {vals.length} points</p>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border"
                      style={{ backgroundColor: col.light, borderColor: col.border, color: col.dark }}>
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: col.hex }} />
                      {selectedParameter.name}
                    </div>
                  </div>
                  <div className="h-60">
                    {chartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                          <defs>
                            <linearGradient id={`mg_${selectedParameter.type}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%"  stopColor={col.hex} stopOpacity={0.22} />
                              <stop offset="95%" stopColor={col.hex} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                          <XAxis dataKey="time" stroke="#d1d5db" fontSize={10} tickLine={false} axisLine={false} />
                          <YAxis stroke="#d1d5db" fontSize={10} tickLine={false} axisLine={false} width={44}
                            tickFormatter={v => `${Number(v).toFixed(0)}${unit}`} />
                          <Tooltip
                            contentStyle={{ background: '#fff', border: `1px solid ${col.border}`, borderRadius: '12px', fontSize: '12px', padding: '10px 14px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}
                            formatter={v => [`${Number(v).toFixed(2)}${unit}`, selectedParameter.name]}
                            labelStyle={{ color: '#374151', fontWeight: 700, marginBottom: 4 }}
                          />
                          <Area type="monotone" dataKey={selectedParameter.type}
                            stroke={col.hex} strokeWidth={2.5}
                            fill={`url(#mg_${selectedParameter.type})`}
                            connectNulls={true}
                            dot={false}
                            activeDot={{ r: 5, fill: col.hex, stroke: '#fff', strokeWidth: 2 }} />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-sm text-gray-400">
                        No data available for this range
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default WeatherStationDashboard;